import type React from "react";

import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { NumericInput } from "@bcc/components/ui/numeric-input";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { Textarea } from "@bcc/components/ui/textarea";
import { useToast } from "@bcc/hooks/use-toast";
import { SessionEndedError } from "@client-shared/providers/session-ended-error";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  ApprovalRequestResult,
  CountryApprovalValues,
  RoasterApprovalRequestDraft,
  WebStatusUpdate,
} from "@shared/services/salesforce/types";
import type { ShipToInfo } from "@shared/types";
import type { CrossBorderCountries } from "@shared/schemas/enums/crossBorder.enum";
import {
  buildDisableEffectiveDate,
  EFFECTIVE_DATE_HELPER_KEY,
  getFirstAvailableEffectiveDate,
  resolveEffectiveDateRule,
} from "@bcc/lib/products/effective-date-rules";
import { buildShipToCurrentValues } from "@bcc/lib/products/ship-to-countries";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApprovalRequestResults } from "./approval-request-results";
import { setChangeAvailabilityPopoverTestId } from "./change-availability-popover.utils";

type VariantStatus = "published" | "out_of_stock" | "rejected" | "draft";
type NextStatus = "published" | "out_of_stock";

export type ChangeAvailabilityVariant = {
  id: string;
  size: string;
  status: VariantStatus;
  sku: string;
  productName: string;
  coffeeTerm: string;
  vendorKey: string;
  // The roaster's own ISO country code (e.g. "DE", "NL", "US"). Becomes the
  // request's roasterCountryCode and the shipToCountryCode of the single
  // ship-to entry for non-cross-border SKUs.
  roasterCountryCode: string;
  // Current weekly inventory for the SKU. Required by the backend on every
  // status-change ship-to entry so the product isn't left stuck mid-inventory
  // without a matching status-change approval.
  weeklyInventory?: number;
  // Per-ship-to-country breakdown from the product fetch (channel → country),
  // carrying each country's current webStatus and weekly inventory.
  shipToInfo?: ShipToInfo[];
  // The SKU's ship-to destination countries (e.g. ["NL", "DE"]). When present
  // the status change emits one entry per country; absent for non-cross-border
  // SKUs, which emit a single roaster-country entry built from `status`.
  shipTo?: CrossBorderCountries[];
  // Current inventory on the SKU as returned by the API. Used to pre-fill
  // the Total Inventory input when a short-term OOS row is being flipped
  // back to Visible for Sale (the typical "restock" flow), so roasters
  // don't have to retype a number that's already on file.
  totalInventory?: number;
  totalMaxShortTermInventory?: number;
};

interface ChangeAvailabilityPopoverProps {
  variant: ChangeAvailabilityVariant;
  onUpdateSkuStatus: (requests: RoasterApprovalRequestDraft[]) => Promise<ApprovalRequestResult[]>;
  isUpdating?: boolean;
  /**
   * Fires once the provider resolves a successful status change. Parent
   * cells use this to lock the trigger out until the next page refresh,
   * preventing duplicate approval requests.
   */
  onSubmitSuccess?: () => void;
  children: React.ReactNode;
  testId?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type ChangeTranslation = (key: string, options?: Record<string, unknown>) => string;

// Visible for Sale ↔ Out of Stock is the only allowed toggle. "Not Visible"
// (draft) rows can only transition to Out of Stock — until the SKU is back
// in a live state the only meaningful next step from a hidden row is to
// mark it as out of stock. Rejected rows keep both options since they
// re-enter the catalogue from approval, not from the storefront.
const getAllowedNextStatuses = (current: VariantStatus): readonly NextStatus[] => {
  if (current === "published") return ["out_of_stock"] as const;
  if (current === "out_of_stock") return ["published"] as const;
  if (current === "draft") return ["out_of_stock"] as const;
  return ["published", "out_of_stock"] as const;
};

const TOTAL_INVENTORY_MAX = 10000;

// The row's current display status mapped to the approval-request enum, used as
// the `currentWebStatus` the backend diffs the requested status against. Draft /
// rejected rows map to "Unpublish" (not visible) — their effective storefront
// state before the change.
const CURRENT_STATUS_TO_WEB_STATUS: Record<VariantStatus, WebStatusUpdate> = {
  published: "Publish",
  out_of_stock: "OutOfStock",
  draft: "Unpublish",
  rejected: "Unpublish",
};

const createSchema = (t: ChangeTranslation, isShortTerm: boolean) =>
  z
    .object({
      status: z.enum(["published", "out_of_stock"]),
      effectiveDate: z.date().optional(),
      notes: z.string().trim().min(1, t("products.changeAvailability.validation.notesRequired")),
      totalInventory: z
        .number()
        .finite(
          t("products.changeAvailability.validation.totalInventoryRange", {
            max: TOTAL_INVENTORY_MAX,
          })
        )
        .int(
          t("products.changeAvailability.validation.totalInventoryRange", {
            max: TOTAL_INVENTORY_MAX,
          })
        )
        .optional()
        .nullable(),
    })
    .superRefine((val, ctx) => {
      if (!val.effectiveDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["effectiveDate"],
          message: t("products.changeAvailability.validation.effectiveDateRequired"),
        });
      }
      if (isShortTerm && val.status === "published") {
        if (typeof val.totalInventory !== "number" || val.totalInventory <= 0 || val.totalInventory > TOTAL_INVENTORY_MAX) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["totalInventory"],
            message: t("products.changeAvailability.validation.totalInventoryRange", { max: TOTAL_INVENTORY_MAX }),
          });
        }
      }
    });

type FormValues = z.infer<ReturnType<typeof createSchema>>;

export function ChangeAvailabilityPopover({
  variant,
  onUpdateSkuStatus,
  isUpdating,
  onSubmitSuccess,
  children,
  testId,
  open: controlledOpen,
  onOpenChange,
}: Readonly<ChangeAvailabilityPopoverProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const tFn = t as ChangeTranslation;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const [effectiveDateOpen, setEffectiveDateOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
    if (!next) setEffectiveDateOpen(false);
  };

  useEffect(() => {
    if (!open) setEffectiveDateOpen(false);
  }, [open]);

  const isShortTerm = variant.coffeeTerm === "Short";
  const schema = useMemo(() => createSchema(tFn, isShortTerm), [tFn, isShortTerm]);
  const allowedStatuses = useMemo(() => getAllowedNextStatuses(variant.status), [variant.status]);
  const defaultNextStatus: NextStatus = allowedStatuses[0] ?? "published";

  const initialEffectiveDateRule = resolveEffectiveDateRule(isShortTerm, variant.status, defaultNextStatus);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    // "all" validates on blur AND change and keeps formState.errors in
    // sync for every field, not just the one being edited — required so
    // the isValid gate and the required-field messages stay accurate
    // when a sibling field (superRefine) turns invalid.
    mode: "all",
    defaultValues: {
      status: defaultNextStatus,
      effectiveDate: getFirstAvailableEffectiveDate(initialEffectiveDateRule),
      notes: "",
      // Pre-fill with the SKU's current `totalInventory` from the API so
      // the short-term OOS → Visible "restock" flow shows the existing
      // value the user is most likely to keep or adjust. Falls back to
      // `totalMaxShortTermInventory` when the API hasn't returned a live
      // figure (e.g. brand-new SKU).
      totalInventory:
        isShortTerm && defaultNextStatus === "published"
          ? (variant.totalInventory ?? variant.totalMaxShortTermInventory ?? undefined)
          : undefined,
    },
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form's form.watch is a subscription API not analyzable by React Compiler
  const watchedStatus = form.watch("status");
  // Total Inventory is short-term-only: required and editable for every
  // short-term transition (V↔OOS, draft/rejected → either). Long-term
  // variants don't use this field.
  const showTotalInventory = isShortTerm;
  const effectiveDateRule = resolveEffectiveDateRule(isShortTerm, variant.status, watchedStatus);
  // Short-term V→OOS relabels the "Effective Date" picker to
  // "Expected Out of Stock" to match the wind-down semantics for short-term
  // SKUs.
  const isShortTermPublishedToOOS = isShortTerm && variant.status === "published" && watchedStatus === "out_of_stock";
  const disableEffectiveDate = useMemo(() => buildDisableEffectiveDate(effectiveDateRule), [effectiveDateRule]);

  const preventClose = useCallback((e: Event) => e.preventDefault(), []);

  useEffect(() => {
    if (open) {
      form.reset({
        status: defaultNextStatus,
        effectiveDate: getFirstAvailableEffectiveDate(initialEffectiveDateRule),
        notes: "",
        totalInventory:
          isShortTerm && defaultNextStatus === "published"
            ? (variant.totalInventory ?? variant.totalMaxShortTermInventory ?? undefined)
            : undefined,
      });
      // Run the resolver once on open so formState.isValid and the
      // required-field errors reflect the empty form before the user
      // interacts. mode: "all" keeps them in sync after that.
      void form.trigger();
      // Short-term OOS → Visible restock flow requires Total Inventory, but
      // when the API hasn't returned an existing value the field is empty
      // and easy to miss — users fill notes, see submit disabled, and have
      // no obvious cue. Focus the input so the cursor lands there and the
      // red aria-invalid border draws the eye.
      const requiresInventoryInput =
        isShortTerm &&
        defaultNextStatus === "published" &&
        variant.totalInventory === undefined &&
        variant.totalMaxShortTermInventory === undefined;
      if (requiresInventoryInput) {
        // Defer one frame so the Radix popover content + Controller-rendered
        // input are mounted before setFocus runs.
        requestAnimationFrame(() => form.setFocus("totalInventory"));
      }
    }
    // reset form state each time the popover opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultNextStatus, variant.totalInventory, variant.totalMaxShortTermInventory, isShortTerm, variant.status]);

  const onValid = async (values: FormValues) => {
    if (!values.effectiveDate) return;
    // webStatus is the same for every ship-to country; the backend rejects a
    // status change whose countries disagree on the requested status.
    const newWebStatus: WebStatusUpdate = values.status === "published" ? "Publish" : "OutOfStock";
    const fallbackCurrentWebStatus = CURRENT_STATUS_TO_WEB_STATUS[variant.status];
    const totalInventory = isShortTerm && typeof values.totalInventory === "number" ? String(values.totalInventory) : undefined;
    // One ship-to entry per destination country (cross-border) or a single
    // roaster-country entry, each carrying the new and current web status plus
    // the existing weekly inventory the backend requires.
    const shipToCountries: CountryApprovalValues[] = buildShipToCurrentValues(
      variant.shipTo ?? [],
      variant.shipToInfo,
      variant.roasterCountryCode,
      { webStatus: fallbackCurrentWebStatus, weeklyInventory: variant.weeklyInventory, totalInventory: variant.totalInventory }
    ).map((entry) => ({
      shipToCountryCode: entry.countryCode,
      webStatus: newWebStatus,
      currentWebStatus: entry.currentWebStatus ?? fallbackCurrentWebStatus,
      weeklyInventory: String(entry.currentWeeklyInventory ?? variant.weeklyInventory ?? 0),
      ...(totalInventory ? { totalInventory } : {}),
    }));
    const request: RoasterApprovalRequestDraft = {
      actionType: "Status Change",
      vendorKey: variant.vendorKey,
      roasterCountryCode: variant.roasterCountryCode,
      itemNumbers: [variant.sku],
      roasterComments: values.notes?.trim() ?? "",
      coffeeTerm: variant.coffeeTerm,
      productName: variant.productName,
      roasterEffectiveDate: format(values.effectiveDate, "yyyy-MM-dd"),
      shipToCountries,
    };
    try {
      const results = await onUpdateSkuStatus([request]);
      toast({
        title: t("products.changeAvailability.submitSuccess"),
        description:
          results.length > 0 ? (
            <span>
              <span>{t("products.approvalRequest.submitSuccessDetail")}</span>
              <ApprovalRequestResults results={results} />
            </span>
          ) : undefined,
        variant: "success",
      });
      onSubmitSuccess?.();
      handleOpenChange(false);
    } catch (error) {
      if (error instanceof SessionEndedError) return;
      toast({
        title: t("common.error", { defaultValue: "Error" }),
        description: t("products.changeAvailability.submitError"),
        variant: "destructive",
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        testId={testId ?? setChangeAvailabilityPopoverTestId("content")}
        className="w-80"
        align="start"
        onInteractOutside={preventClose}
        onEscapeKeyDown={preventClose}
      >
        <form onSubmit={form.handleSubmit(onValid)} className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">{t("products.changeAvailability.title")}</h4>
            <p className="text-xs text-muted-foreground">
              {t("products.changeAvailability.description", {
                size: variant.size,
              })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="change-availability-term" className="text-xs">
              {t("products.changeAvailability.term")}
            </Label>
            <Input
              id="change-availability-term"
              data-testid={setChangeAvailabilityPopoverTestId("term")}
              value={isShortTerm ? t("views.productsTable.term.shortTerm") : t("views.productsTable.term.longTerm")}
              disabled
              readOnly
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="change-availability-status" className="text-xs">
              {t("products.changeAvailability.status")}
            </Label>
            <Controller
              control={form.control}
              name="status"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="change-availability-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedStatuses.includes("published") && (
                      <SelectItem value="published">{t("products.changeAvailability.statusOptions.published")}</SelectItem>
                    )}
                    {allowedStatuses.includes("out_of_stock") && (
                      <SelectItem value="out_of_stock">{t("products.changeAvailability.statusOptions.outOfStock")}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {showTotalInventory && (
            <div className="space-y-2">
              <Label htmlFor="change-availability-total-inventory" className="text-xs">
                {t("products.changeAvailability.totalInventory")}
                <span aria-hidden="true" className="ml-0.5 text-destructive">
                  *
                </span>
              </Label>
              <Controller
                control={form.control}
                name="totalInventory"
                render={({ field }) => (
                  <NumericInput
                    id="change-availability-total-inventory"
                    data-testid={setChangeAvailabilityPopoverTestId("totalInventory")}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    aria-invalid={form.formState.errors.totalInventory ? true : undefined}
                  />
                )}
              />
              {form.formState.errors.totalInventory && (
                <p data-testid={setChangeAvailabilityPopoverTestId("totalInventory-error")} className="text-xs text-destructive">
                  {form.formState.errors.totalInventory.message}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label data-testid={setChangeAvailabilityPopoverTestId("effectiveDate-label")} className="text-xs">
              {t(
                isShortTermPublishedToOOS
                  ? "products.changeAvailability.expectedOutOfStockDate"
                  : "products.changeAvailability.effectiveDate"
              )}
              <span aria-hidden="true" className="ml-0.5 text-destructive">
                *
              </span>
            </Label>
            <Controller
              control={form.control}
              name="effectiveDate"
              render={({ field }) => (
                <Popover open={effectiveDateOpen} onOpenChange={setEffectiveDateOpen} modal>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      data-testid={setChangeAvailabilityPopoverTestId("effectiveDate-trigger")}
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-transparent"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? format(field.value, "PPP") : t("products.changeAvailability.selectDate")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      // Open the picker on the month of the auto-filled /
                      // selected date so users with an auto-fill date in a
                      // future month don't have to navigate forward to find
                      // their selection (e.g. today is Apr 29 but the
                      // first-available date is May 3 — show May).
                      defaultMonth={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setEffectiveDateOpen(false);
                      }}
                      disabled={disableEffectiveDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
            />
            <p data-testid={setChangeAvailabilityPopoverTestId("effectiveDate-helper")} className="text-xs text-muted-foreground">
              {tFn(EFFECTIVE_DATE_HELPER_KEY[effectiveDateRule])}
            </p>
            {form.formState.errors.effectiveDate && (
              <p data-testid={setChangeAvailabilityPopoverTestId("effectiveDate-error")} className="text-xs text-destructive">
                {form.formState.errors.effectiveDate.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="change-availability-notes" className="text-xs">
              {t("products.changeAvailability.notes")}
              <span aria-hidden="true" className="ml-0.5 text-destructive">
                *
              </span>
            </Label>
            <Textarea
              id="change-availability-notes"
              placeholder={t("products.changeAvailability.notesPlaceholder")}
              rows={3}
              aria-invalid={form.formState.errors.notes ? true : undefined}
              {...form.register("notes")}
            />
            {form.formState.errors.notes && (
              <p data-testid={setChangeAvailabilityPopoverTestId("notes-error")} className="text-xs text-destructive">
                {form.formState.errors.notes.message}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => handleOpenChange(false)} className="flex-1">
              {t("common.cancel")}
            </Button>
            <Button type="submit" size="sm" disabled={!form.formState.isValid || isUpdating} className="flex-1 bg-primary">
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUpdating ? t("products.changeAvailability.submitting") : t("products.changeAvailability.submitForApproval")}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
